import { useState } from 'react';
import { Button, Center, Group, Loader, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useObject, useRenameObject, type DataObject } from '@/api/objects';
import { useProject } from '@/api/projects';
import { EmptyState } from '@/components/EmptyState';
import { InlineRename } from '@/components/InlineRename';
import { ButtonLink } from '@/components/links';
import { usePageTitle } from '@/components/usePageTitle';
import { DeleteObjectModal } from '@/features/objects/DeleteObjectModal';
import { objectName } from '@/features/objects/objectName';
import { UploadPanel } from '@/features/objects/UploadPanel';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { ClientShell } from '@/shells/ClientShell';
import { useUpload } from '@/upload/useUpload';

export const Route = createFileRoute('/_client/projects_/$projectId/upload/$objectId')({ component: UploadPage });

function UploadPage() {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const { projectId, objectId } = Route.useParams();
  const project = useProject(projectId);
  const object = useObject(objectId);
  const kindLabel = useKindLabel();
  const rename = useRenameObject(objectId);
  const navigate = useNavigate();
  const upload = useUpload(objectId);
  const [toDelete, setToDelete] = useState<DataObject | null>(null);
  usePageTitle(t('upload.title'));

  const title = object.data ? kindLabel(object.data.kind) : t('upload.title');
  const busy =
    upload.state.phase === 'hashing' ||
    upload.state.phase === 'opening' ||
    upload.state.phase === 'uploading' ||
    upload.state.phase === 'verifying';
  return (
    <ClientShell eyebrow={project.data?.name} title={title}>
      <Stack gap="md">
        {object.data && (
          <InlineRename
            value={objectName(object.data)}
            canEdit={!!object.data.can_rename}
            onSave={async (name) => {
              try {
                await rename.mutateAsync(name);
              } catch (err) {
                notifications.show({ color: 'red', message: errorMessage(err) });
                throw err;
              }
            }}
          />
        )}
        {object.isPending ? (
          <Center py="xl">
            <Loader color="forest" />
          </Center>
        ) : object.isError ? (
          <EmptyState title={errorMessage(object.error)} />
        ) : (
          <UploadPanel object={object.data} upload={upload} />
        )}
        <Group>
          <ButtonLink variant="default" to="/projects/$projectId" params={{ projectId }}>
            {t('upload.backToProject')}
          </ButtonLink>
          {object.data?.removable && !busy && (
            <Button
              color="red"
              variant="light"
              onClick={() => {
                upload.cancel();
                setToDelete(object.data);
              }}
            >
              {tc('common.delete')}
            </Button>
          )}
        </Group>
        <DeleteObjectModal
          object={toDelete}
          onClose={() => setToDelete(null)}
          onRemoved={() => void navigate({ to: '/projects/$projectId', params: { projectId } })}
        />
      </Stack>
    </ClientShell>
  );
}
